import React, { useEffect, useState } from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import {
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import {
    DownvoteReason,
    FeedbackVote,
    getFeedbackVoteIcon,
    getFeedbackVoteLabel,
    ProjectPaperFeedback,
} from '../../lib/projectPaperFeedback';
import { getDownvoteReasonLabel } from './projectFeedUi';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import type { MessageDescriptor } from '@lingui/core';

interface ProjectFeedFeedbackControlProps {
    feedback: ProjectPaperFeedback | null;
    pending: boolean;
    onSetFeedback: (vote: FeedbackVote, reason?: DownvoteReason) => Promise<void>;
    onClearFeedback: () => Promise<void>;
    showHeader?: boolean;
    buttonAlignment?: 'center' | 'left';
}

interface VoteOption {
    value: FeedbackVote;
    icon: 'thumbs-up' | 'thumbs-down' | 'star';
}

const VOTE_OPTIONS: VoteOption[] = [
    { value: 'downvote', icon: getFeedbackVoteIcon('downvote') },
    { value: 'upvote', icon: getFeedbackVoteIcon('upvote') },
    { value: 'star', icon: getFeedbackVoteIcon('star') },
];

const REASON_OPTIONS: Array<{ value: Exclude<DownvoteReason, null>; label: MessageDescriptor }> = [
    { value: 'not_relevant', label: msg({ id: 'feedback.reason.offTopic', message: 'Off-topic' }) },
    { value: 'low_quality', label: msg({ id: 'feedback.reason.lowQuality', message: 'Low quality' }) },
];

export default function ProjectFeedFeedbackControl({
    feedback,
    pending,
    onSetFeedback,
    onClearFeedback,
    showHeader = true,
    buttonAlignment = 'center',
}: ProjectFeedFeedbackControlProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const currentVote = feedback?.vote ?? null;
    const currentReason = feedback?.downvote_reason ?? null;
    const currentReasonLabel = getDownvoteReasonLabel(currentReason);
    const [isReasonPickerOpen, setIsReasonPickerOpen] = useState(false);

    useEffect(() => {
        if (currentVote !== null || currentReason !== null) {
            setIsReasonPickerOpen(false);
        }
    }, [currentReason, currentVote]);

    const handleVotePress = async (vote: FeedbackVote) => {
        if (pending) {
            return;
        }

        if (vote === 'downvote') {
            if (currentVote === 'downvote') {
                setIsReasonPickerOpen((current) => !current);
                return;
            }

            setIsReasonPickerOpen((current) => !current);
            return;
        }

        setIsReasonPickerOpen(false);
        if (currentVote === vote) {
            await onClearFeedback();
            return;
        }

        await onSetFeedback(vote, null);
    };

    const handleReasonPress = async (reason: Exclude<DownvoteReason, null>) => {
        if (pending) {
            return;
        }

        setIsReasonPickerOpen(false);
        await onSetFeedback('downvote', reason);
    };

    const shouldShowReasonSection = isReasonPickerOpen || (currentVote === 'downvote' && !currentReason);
    const isChoosingReason = isReasonPickerOpen;

    return (
        <View style={styles.container}>
            {showHeader ? (
                <View style={styles.headerRow}>
                    <Text style={[styles.label, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeedfeedbackcontrol.feedback" comment="UI copy in components/feed/ProjectFeedFeedbackControl.tsx">
                        Feedback
                    </Trans></Text>
                </View>
            ) : null}

            <View
                style={[
                    styles.segmentedControl,
                    buttonAlignment === 'left' && styles.segmentedControlLeft,
                ]}
            >
                {VOTE_OPTIONS.map((option) => {
                    const isActive = currentVote === option.value;
                    const label = option.value === 'downvote' && isActive && currentReasonLabel
                        ? currentReasonLabel
                        : getFeedbackVoteLabel(option.value);

                    return (
                        <TouchableOpacity
                            key={option.value}
                            style={[styles.segmentButton, isActive && styles.segmentButtonActive]}
                            onPress={() => { void handleVotePress(option.value); }}
                            activeOpacity={0.72}
                            disabled={pending}
                            testID={`project-feed-feedback-${option.value}`}
                        >
                            <FontAwesome6
                                name={option.icon}
                                size={12}
                                color={isActive ? colors.primaryGreenDark : colors.text600}
                                solid={option.icon === 'star' && isActive}
                            />
                            <Text
                                style={[
                                    styles.segmentText,
                                isActive && styles.segmentTextActive,
                                fontsLoaded && interSemiBold,
                            ]}
                        >
                                {i18n._(label)}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {shouldShowReasonSection ? (
                <View
                    style={[
                        styles.reasonSection,
                        isChoosingReason && styles.reasonSectionPrompt,
                    ]}
                    testID="project-feed-reason-section"
                >
                    <View style={styles.reasonHeader}>
                        <Text style={[styles.reasonLabel, fontsLoaded && interSemiBold]}>
                            {isChoosingReason ? <Trans id="feed.projectfeedfeedbackcontrol.chooseAReasonToSave" comment="Conditional UI copy in components/feed/ProjectFeedFeedbackControl.tsx">Choose a reason to save</Trans> : <Trans id="feed.projectfeedfeedbackcontrol.reason" comment="Conditional UI copy in components/feed/ProjectFeedFeedbackControl.tsx">Reason</Trans>}
                        </Text>
                        {currentReasonLabel ? (
                            <Text style={[styles.reasonValue, fontsLoaded && interRegular]}>
                                {i18n._(currentReasonLabel)}
                            </Text>
                        ) : null}
                    </View>

                    {isChoosingReason ? (
                        <Text style={[styles.reasonPromptText, fontsLoaded && interRegular]}><Trans id="feed.projectfeedfeedbackcontrol.finishMarkingThisPaperAsNotForMe" comment="UI copy in components/feed/ProjectFeedFeedbackControl.tsx">
                            Finish marking this paper as Not for me.
                        </Trans></Text>
                    ) : null}

                    <View style={styles.reasonRow}>
                        {REASON_OPTIONS.map((option) => {
                            const isActive = currentReason === option.value;

                            return (
                                <TouchableOpacity
                                    key={option.value}
                                    style={[styles.reasonChip, isActive && styles.reasonChipActive]}
                                    onPress={() => { void handleReasonPress(option.value); }}
                                    activeOpacity={0.72}
                                    disabled={pending}
                                    testID={`project-feed-reason-${option.value}`}
                                >
                                    <Text
                                        style={[
                                            styles.reasonChipText,
                                            isActive && styles.reasonChipTextActive,
                                            fontsLoaded && interSemiBold,
                                        ]}
                                    >
                                        {i18n._(option.label)}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 24,
        width: '100%',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
    },
    label: {
        fontSize: 12,
        color: colors.text500,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },
    segmentedControl: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    segmentedControlLeft: {
        justifyContent: 'flex-start',
    },
    segmentButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#d9dde5',
        backgroundColor: '#ffffff',
        paddingHorizontal: 14,
        paddingVertical: 10,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    segmentButtonActive: {
        borderColor: '#c3d3bc',
        backgroundColor: '#f4f8f0',
    },
    segmentText: {
        fontSize: 13,
        color: colors.text700,
    },
    segmentTextActive: {
        color: colors.primaryGreenDark,
    },
    reasonSection: {
        marginTop: 16,
        gap: 10,
        borderRadius: 14,
        padding: 14,
        borderWidth: 1,
        borderColor: '#dde2ea',
        backgroundColor: '#fafbfd',
    },
    reasonSectionPrompt: {
        borderColor: '#d7dfcf',
        backgroundColor: '#f7faf3',
    },
    reasonHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    reasonLabel: {
        fontSize: 12,
        color: colors.text500,
        textTransform: 'uppercase',
    },
    reasonValue: {
        fontSize: 12,
        color: colors.text600,
    },
    reasonPromptText: {
        fontSize: 13,
        lineHeight: 20,
        color: colors.text600,
    },
    reasonRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    reasonChip: {
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#dde2ea',
        backgroundColor: '#ffffff',
        paddingHorizontal: 12,
        paddingVertical: 8,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    reasonChipActive: {
        borderColor: '#c3d3bc',
        backgroundColor: '#f4f8f0',
    },
    reasonChipText: {
        fontSize: 12,
        color: colors.text700,
    },
    reasonChipTextActive: {
        color: colors.primaryGreenDark,
    },
});
